import React, { useEffect, useState, useRef } from "react";
import { useParams } from "react-router-dom";
import { Star, Globe, Clock, X } from "lucide-react";
import { Check, PlayCircle } from "lucide-react";
import {
    FaVideo,
    FaFileDownload,
    FaMobileAlt,
    FaInfinity,
    FaCertificate
} from 'react-icons/fa';
import { getCourseBySlug } from "../api/api";
import Plyr from 'plyr';
import 'plyr/dist/plyr.css';
import { useNavigate } from 'react-router-dom';

const CoursesDetailsHome = () => {
    const navigate = useNavigate();
    const { slug } = useParams();
    const [course, setCourse] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [activeModule, setActiveModule] = useState(null);
    const [showVideoModal, setShowVideoModal] = useState(false);
    const videoRef = useRef(null);
    const playerRef = useRef(null);

    const handleAddToCart = () => {
        navigate('/cart');
    };


    useEffect(() => {
        const fetchCourse = async () => {
            try {
                setLoading(true);
                const response = await getCourseBySlug(slug);
                console.log("Course Details Response:", response);

                if (response && response.success === true) {
                    setCourse(response.course);
                    setError(null);
                    if (response.course.modules && response.course.modules.length > 0) {
                        setActiveModule(response.course.modules[0].id);
                    }
                } else {
                    setCourse(null);
                    setError(response?.message || "Course not found");
                }
            } catch (error) {
                console.error("Error fetching course:", error);
                setCourse(null);
                setError(error.message);
            } finally {
                setLoading(false);
            }
        };

        if (slug) {
            fetchCourse();
        }
    }, [slug]);

    // Initialize Plyr player when modal opens
    useEffect(() => {
        if (showVideoModal && videoRef.current && course?.intro_video_url) {
            // Destroy existing player if any
            if (playerRef.current) {
                playerRef.current.destroy();
            }

            // Initialize new player
            playerRef.current = new Plyr(videoRef.current, {
                controls: [
                    'play', 'progress', 'current-time', 'duration',
                    'mute', 'volume', 'fullscreen'
                ],
                settings: ['quality', 'speed'],
                speed: { selected: 1, options: [0.5, 0.75, 1, 1.25, 1.5, 2] },
                quality: { default: 720, options: [1080, 720, 480] }
            });
        }

        // Cleanup on modal close
        return () => {
            if (playerRef.current) {
                playerRef.current.destroy();
                playerRef.current = null;
            }
        };
    }, [showVideoModal, course]);

    const openVideoModal = () => {
        setShowVideoModal(true);
        document.body.style.overflow = 'hidden';
    };

    const closeVideoModal = () => {
        setShowVideoModal(false);
        document.body.style.overflow = 'auto';
        // Pause video when closing
        if (playerRef.current) {
            playerRef.current.pause();
        }
    };

    const formatDuration = (minutes) => {
        if (!minutes) return "0 min";
        const hours = Math.floor(minutes / 60);
        const mins = minutes % 60;
        if (hours > 0) {
            return `${hours}h ${mins}m`;
        }
        return `${mins}m`;
    };

    if (loading) {
        return (
            <div className="flex justify-center items-center py-20">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-500"></div>
            </div>
        );
    }

    if (error || !course) {
        return (
            <div className="text-center py-20">
                <p className="text-red-500">{error || "Course not found"}</p>
            </div>
        );
    }

    return (
        <>
            <div className="bg-[#1f2a37] text-white py-16 px-6">
                <div className="max-w-6xl mx-auto">
                    <p className="text-sm text-gray-400 mb-4 font-lato">
                        <span className="text-green-400 font-lato">Home</span> &gt;{" "}
                        <span className="text-green-400 font-lato">Courses</span> &gt;{" "}
                        {course.title}
                    </p>

                    <h1 className="text-3xl md:text-5xl leading-tight mb-4 font-merriweather">
                        {course.title}
                    </h1>

                    <p className="text-gray-300 max-w-2xl mb-6 font-lato">
                        {course.short_description}
                    </p>

                    <div className="flex flex-wrap items-center gap-4 mb-6 text-sm font-lato">
                        {course.is_bestseller && (
                            <span className="bg-gray-700 px-3 py-1 rounded-md">
                                Bestseller
                            </span>
                        )}

                        <div className="flex items-center gap-1 text-yellow-400 font-lato">
                            <span className="text-white font-medium mr-1">{course.rating_display || course.rating}</span>
                            {[...Array(5)].map((_, i) => (
                                <Star key={i} size={14} fill={i < Math.floor(course.rating) ? "currentColor" : "none"} />
                            ))}
                        </div>

                        <span className="text-gray-400">({course.total_ratings || 0} ratings)</span>
                        <span className="text-gray-400">{course.students_enrolled || 0} students enrolled</span>
                    </div>

                    <div className="flex items-center gap-3 mb-4">
                        <img
                            src={course.instructor?.profile_image}
                            alt="author"
                            className="w-10 h-10 rounded-full object-cover"
                        />
                        <p className="text-sm text-gray-300 font-lato">
                            Created by{" "}
                            <span className="text-white font-medium font-lato">
                                {course.instructor_name}
                            </span>
                        </p>
                    </div>

                    <div className="flex items-center gap-6 text-sm text-gray-400 font-lato">
                        <div className="flex items-center gap-1 font-lato">
                            <Clock size={14} />
                            Last updated {new Date(course.last_updated).toLocaleDateString()}
                        </div>
                        <div className="flex items-center gap-1 font-lato">
                            <Globe size={14} />
                            {course.language}
                        </div>
                    </div>
                </div>
            </div>

            <div className="bg-[#f5f5f5] py-16 px-6">
                <div className="max-w-6xl mx-auto grid md:grid-cols-3 gap-10">
                    <div className="md:col-span-2">
                        {course.what_you_learn && course.what_you_learn.length > 0 && (
                            <div className="bg-white p-8 rounded-xl shadow-sm border mb-10">
                                <h2 className="text-xl font-semibold text-gray-800 mb-6 font-merriweather">
                                    What you'll learn
                                </h2>
                                <div className="grid md:grid-cols-2 gap-4 font-lato">
                                    {course.what_you_learn.map((item, index) => (
                                        <div key={index} className="flex items-start gap-2">
                                            <Check className="text-green-500 w-4 h-4 mt-1" />
                                            <p className="text-sm text-gray-600">{item.replace(/\r/g, '')}</p>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}

                        <div className="bg-white p-8 rounded-xl shadow-sm border">
                            <h2 className="text-xl font-semibold text-gray-800 mb-6 font-merriweather">
                                Course Description
                            </h2>
                            <div className="border-t mb-6"></div>
                            <div className="text-gray-600 text-sm leading-relaxed font-lato whitespace-pre-line">
                                {course.long_description}
                            </div>
                        </div>

                        {course.modules && course.modules.length > 0 && (
                            <div className="bg-white p-8 rounded-xl shadow-sm border mt-10">
                                <div className="flex justify-between items-center mb-4">
                                    <h2 className="text-xl font-semibold text-gray-800 font-merriweather">
                                        Course Syllabus
                                    </h2>
                                    <span
                                        className="text-green-500 text-sm cursor-pointer"
                                        onClick={() => {
                                            if (activeModule === "all") {
                                                setActiveModule(course.modules[0]?.id);
                                            } else {
                                                setActiveModule("all");
                                            }
                                        }}
                                    >
                                        {activeModule === "all" ? "Collapse all sections" : "Expand all sections"}
                                    </span>
                                </div>

                                <div className="border-t mb-6"></div>

                                <p className="text-sm text-gray-500 mb-6">
                                    {course.modules.length} Modules • {course.statistics?.total_lectures || 0} Lectures • {course.statistics?.total_duration || "0 min"} total length
                                </p>

                                <div className="space-y-4">
                                    {course.modules.map((module) => (
                                        <div key={module.id} className="border rounded-lg overflow-hidden">
                                            <div
                                                onClick={() => setActiveModule(activeModule === module.id ? null : module.id)}
                                                className="flex justify-between items-center bg-gray-50 px-5 py-4 cursor-pointer hover:bg-gray-100 transition"
                                            >
                                                <p className="text-sm font-medium text-gray-800">
                                                    {module.title}
                                                </p>
                                                <p className="text-xs text-gray-500">
                                                    {module.lectures_count} lectures • {formatDuration(module.duration_minutes)}
                                                </p>
                                            </div>

                                            {(activeModule === module.id || activeModule === "all") && module.data_videos_attachment && module.data_videos_attachment.length > 0 && (
                                                <div className="bg-white px-5 py-4 space-y-3">
                                                    {module.data_videos_attachment.map((item) => (
                                                        <div key={item.id} className="flex justify-between text-sm text-gray-600">
                                                            <p className="flex items-center gap-2">
                                                                {item.type === "video" ? "▶" : "📄"} {item.title}
                                                            </p>
                                                            <span className="text-gray-400">
                                                                {item.duration_formatted || item.file_size_formatted || "Preview"}
                                                            </span>
                                                        </div>
                                                    ))}
                                                </div>
                                            )}
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}

                        {course.instructor && (
                            <div className="bg-white p-8 rounded-xl shadow-sm border mt-10">
                                <h2 className="text-xl font-semibold text-gray-800 mb-6 font-merriweather">
                                    Meet Your Instructor
                                </h2>
                                <div className="border-t mb-6"></div>

                                <div className="flex flex-col md:flex-row gap-6">
                                    <div className="flex-shrink-0">
                                        <img
                                            src={course.instructor.profile_image}
                                            alt={course.instructor.name}
                                            className="w-24 h-24 rounded-full object-cover"
                                        />
                                    </div>

                                    <div>
                                        <h3 className="text-lg font-semibold text-gray-800 font-merriweather">
                                            {course.instructor.name} {course.instructor.credentials && `(${course.instructor.credentials})`}
                                        </h3>
                                        <p className="text-green-600 text-sm font-medium mt-1 font-lato">
                                            {course.instructor.title}
                                        </p>

                                        <div className="flex flex-wrap items-center gap-4 text-sm text-gray-500 mt-2 font-lato">
                                            <span className="flex items-center gap-1 text-yellow-500">
                                                ⭐ {course.instructor.rating} Instructor Rating
                                            </span>
                                            <span>👥 {course.instructor.total_students} Students</span>
                                            <span>📚 {course.instructor.total_courses} Courses</span>
                                        </div>

                                        <p className="text-gray-600 text-sm leading-relaxed mt-4 max-w-2xl font-lato">
                                            {course.instructor.bio}
                                        </p>
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>

                    <div className="relative md:-mt-40 z-10">
                        <div className="bg-white rounded-xl shadow-xl border border-gray-200 p-6 top-6 self-start">
                            <div className="relative mb-5 cursor-pointer group" onClick={openVideoModal}>
                                <img
                                    src={course.image_url_full}
                                    alt={course.title}
                                    className="rounded-lg w-full h-44 object-cover"
                                />
                                {course.intro_video_url && (
                                    <div className="absolute inset-0 flex items-center justify-center bg-black/30 group-hover:bg-black/40 transition-all">
                                        <div className="bg-white p-3 rounded-full shadow-md hover:scale-110 transition-transform">
                                            <PlayCircle className="text-green-500 w-10 h-10" />
                                        </div>
                                    </div>
                                )}
                            </div>

                            <div className="flex items-center gap-3 mb-4 font-lato">
                                <p className="text-2xl font-bold text-gray-900">
                                    {course.discounted_price_formatted || course.price_formatted}
                                </p>
                                {course.discounted_price && (
                                    <p className="text-sm text-gray-400 line-through">
                                        {course.price_formatted}
                                    </p>
                                )}
                                {course.discount_percentage && (
                                    <p className="text-sm text-red-500 font-medium ml-auto">
                                        {course.discount_percentage}% Off
                                    </p>
                                )}
                            </div>

                            <button className="w-full bg-green-500 text-white py-2.5 rounded-lg font-lato mb-3 hover:bg-green-600 transition font-medium">
                                Enroll Now
                            </button>
                            <button
                                className="w-full font-lato border border-green-500 text-green-600 py-2.5 rounded-lg hover:bg-green-50 transition font-medium"
                                onClick={handleAddToCart}
                            >
                                Add to Cart
                            </button>

                            <p className="text-center text-xs text-gray-400 mt-3 font-lato">
                                30-Day Money-Back Guarantee
                            </p>

                            {course.includes && course.includes.length > 0 && (
                                <div className="mt-5 border-t pt-4">
                                    <p className="text-sm font-medium text-gray-700 mb-2 font-lato">
                                        This course includes:
                                    </p>
                                    <ul className="space-y-2 text-sm text-gray-600 font-lato">
                                        {course.includes.slice(0, 5).map((item, index) => (
                                            <li key={index} className="flex items-center gap-2">
                                                {item.includes("video") ? (
                                                    <FaVideo className="w-4 h-4 text-gray-500" />
                                                ) : item.includes("download") ? (
                                                    <FaFileDownload className="w-4 h-4 text-gray-500" />
                                                ) : item.includes("mobile") ? (
                                                    <FaMobileAlt className="w-4 h-4 text-gray-500" />
                                                ) : item.includes("lifetime") ? (
                                                    <FaInfinity className="w-4 h-4 text-gray-500" />
                                                ) : item.includes("Certificate") ? (
                                                    <FaCertificate className="w-4 h-4 text-gray-500" />
                                                ) : (
                                                    <FaVideo className="w-4 h-4 text-gray-500" />
                                                )}
                                                <span>{item.replace(/\r/g, '')}</span>
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                            )}

                            <div className="flex justify-between mt-5 text-xs text-gray-500 border-t pt-3">
                                <span className="cursor-pointer hover:underline font-lato">Share</span>
                                <span className="cursor-pointer hover:underline font-lato">Gift this course</span>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            {showVideoModal && (
                <div className="fixed inset-0 bg-black/90 z-50 flex items-center justify-center p-4">
                    <div className="relative w-full max-w-4xl">
                        <button
                            onClick={closeVideoModal}
                            className="absolute -top-12 right-0 text-white hover:text-gray-300 transition"
                        >
                            <X size={32} />
                        </button>
                        <div className="bg-black rounded-lg overflow-hidden">
                            <video
                                ref={videoRef}
                                className="plyr-react plyr"
                                playsInline
                                controls
                            >
                                <source src={course.intro_video_url} type="video/mp4" />
                                Your browser does not support the video tag.
                            </video>
                        </div>
                        <div className="mt-4 text-white">
                            <p className="text-gray-400 text-sm mt-1">{course.title}</p>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
};

export default CoursesDetailsHome;